import { useState } from "react";
import axios from "axios";
import { Phone, Mail, MapPin, Send, Clock } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const initial = { name: "", email: "", phone: "", service: "", message: "" };

const serviceOptions = [
  "GST Services",
  "Income Tax Filing",
  "Accounting & Bookkeeping",
  "Business Advisory",
  "Project Finance & Loan",
  "International Services",
  "Business Registration",
  "Other",
];

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (!form.name || !form.email || !form.phone || !form.message) {
      toast.error("Please fill in name, email, phone and message.");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        phone: form.phone,
        service: form.service || null,
        message: form.message,
      });
      toast.success("Thank you! We'll get back to you shortly.");
      setForm(initial);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof detail === "string" ? detail : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* HEADER */}
      <section data-testid="contact-header" className="page-offset bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 text-center">
          <span className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37] font-semibold">
            Contact Us
          </span>
          <h1 className="mt-6 font-display font-light text-[#0A1F44] text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            Let's start a <span className="italic">conversation.</span>
          </h1>
          <div className="mt-8 h-[2px] w-14 bg-[#D4AF37] mx-auto" />
          <p className="mt-6 text-[15px] md:text-base leading-relaxed text-[#4B5563] max-w-2xl mx-auto">
            Share a few details and our team will reach out within one business day.
          </p>
        </div>
      </section>

      {/* CONTENT */}
      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-5 gap-12">
          {/* FORM */}
          <form
            onSubmit={onSubmit}
            data-testid="contact-form"
            className="lg:col-span-3 bg-white border border-gray-200 p-8 md:p-10"
            noValidate
          >
            <h2 className="font-display text-2xl md:text-3xl text-[#0A1F44] font-medium">
              Request a consultation
            </h2>
            <p className="mt-2 text-[14px] text-[#4B5563]">
              Fill the form — we'll get back to you within one business day.
            </p>

            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-[11px] uppercase tracking-[0.22em] text-[#0A1F44] font-semibold mb-2">
                  Full Name *
                </label>
                <input
                  name="name"
                  type="text"
                  value={form.name}
                  onChange={onChange}
                  data-testid="contact-name"
                  required
                  className="w-full border border-gray-300 px-4 py-3 text-[15px] bg-white focus:outline-none focus:border-[#D4AF37] transition-colors"
                  placeholder="Your name"
                />
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-[0.22em] text-[#0A1F44] font-semibold mb-2">
                  Email *
                </label>
                <input
                  name="email"
                  type="email"
                  value={form.email}
                  onChange={onChange}
                  data-testid="contact-email"
                  required
                  className="w-full border border-gray-300 px-4 py-3 text-[15px] bg-white focus:outline-none focus:border-[#D4AF37] transition-colors"
                  placeholder="you@company.com"
                />
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-[0.22em] text-[#0A1F44] font-semibold mb-2">
                  Phone *
                </label>
                <input
                  name="phone"
                  type="tel"
                  value={form.phone}
                  onChange={onChange}
                  data-testid="contact-phone"
                  required
                  className="w-full border border-gray-300 px-4 py-3 text-[15px] bg-white focus:outline-none focus:border-[#D4AF37] transition-colors"
                  placeholder="+91 ..."
                />
              </div>
              <div>
                <label className="block text-[11px] uppercase tracking-[0.22em] text-[#0A1F44] font-semibold mb-2">
                  Service of Interest
                </label>
                <select
                  name="service"
                  value={form.service}
                  onChange={onChange}
                  data-testid="contact-service"
                  className="w-full border border-gray-300 px-4 py-[14px] text-[15px] bg-white focus:outline-none focus:border-[#D4AF37] transition-colors"
                >
                  <option value="">Select a service</option>
                  {serviceOptions.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="mt-6">
              <label className="block text-[11px] uppercase tracking-[0.22em] text-[#0A1F44] font-semibold mb-2">
                Message *
              </label>
              <textarea
                name="message"
                value={form.message}
                onChange={onChange}
                data-testid="contact-message"
                required
                rows={5}
                className="w-full border border-gray-300 px-4 py-3 text-[15px] bg-white focus:outline-none focus:border-[#D4AF37] transition-colors resize-none"
                placeholder="Briefly describe what you need help with..."
              />
            </div>

            <button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit"
              className="mt-8 inline-flex items-center gap-3 bg-[#0A1F44] text-white px-8 py-4 font-semibold text-[14px] uppercase tracking-[0.14em] hover:bg-[#D4AF37] hover:text-[#0A1F44] transition-colors disabled:opacity-70 disabled:cursor-not-allowed"
            >
              {submitting ? "Sending..." : "Send Message"}
              <Send size={16} />
            </button>
          </form>

          {/* DETAILS */}
          <aside className="lg:col-span-2">
            <div className="bg-[#0A1F44] text-white p-8 md:p-10">
              <span className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37] font-semibold">
                Reach Us
              </span>
              <h3 className="mt-4 font-display text-2xl md:text-3xl leading-tight">
                We're here to advise, assist &amp; achieve — together.
              </h3>
              <div className="mt-8 space-y-6">
                <div className="flex items-start gap-4" data-testid="contact-detail-phone">
                  <div className="w-10 h-10 flex items-center justify-center bg-white/5 text-[#D4AF37]">
                    <Phone size={18} strokeWidth={1.75} />
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37]">Phone</div>
                    <a href="tel:+917487978020" className="mt-1 block text-white hover:text-[#D4AF37] transition-colors">
                      +91 74879 78020
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-testid="contact-detail-email">
                  <div className="w-10 h-10 flex items-center justify-center bg-white/5 text-[#D4AF37]">
                    <Mail size={18} strokeWidth={1.75} />
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37]">Email</div>
                    <a href="mailto:info@avvantaadvisory.com" className="mt-1 block text-white hover:text-[#D4AF37] transition-colors break-all">
                      info@avvantaadvisory.com
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-testid="contact-detail-location">
                  <div className="w-10 h-10 flex items-center justify-center bg-white/5 text-[#D4AF37]">
                    <MapPin size={18} strokeWidth={1.75} />
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37]">Location</div>
                    <div className="mt-1 text-white">Surat, Gujarat, India</div>
                  </div>
                </div>
                <div className="flex items-start gap-4" data-testid="contact-detail-hours">
                  <div className="w-10 h-10 flex items-center justify-center bg-white/5 text-[#D4AF37]">
                    <Clock size={18} strokeWidth={1.75} />
                  </div>
                  <div>
                    <div className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37]">Office Hours</div>
                    <div className="mt-1 text-white">Mon – Sat · 10:00 to 19:00 IST</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-6 border border-gray-200 p-6 text-[14px] text-[#4B5563] leading-relaxed">
              Prefer WhatsApp? Tap the green button in the corner to start a quick chat with our team.
            </div>
          </aside>
        </div>
      </section>
    </>
  );
}
